import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import CompanyProfileSchema from "marketplace/companies/company-profile-schema";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import { z } from "zod";

export type CompanyJSON = z.infer<typeof CompanySchema.CompanyJSONZ>;
export type CompanyParams = z.infer<typeof CompanySchema.CompanyZ>;

export default class CompanySchema {
  public static readonly CompanyJSONZ = z.object({
    ...CompanyProfileSchema.CompanyProfileJSONZ.shape,
    id: z.uuid(),
    legalEntityId: z.uuid(),
    contactId: z.uuid().nullish(),
    isVisible: z.boolean().nullish(),
  });

  public static readonly CompanyZ = z.object({
    id: z.instanceof(Guid),
    legalEntityId: z.instanceof(Guid),
    profile: z.instanceof(CompanyProfile).optional(),
    contactId: z.instanceof(Guid).optional(),
    isVisible: z.boolean().optional(),
  });

  public static validate(values: unknown): CompanyParams {
    return parseOrThrow(CompanySchema.CompanyZ, values, {
      logError: true,
      schemaName: "CompanySchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): CompanyJSON {
    return parseOrThrow(CompanySchema.CompanyJSONZ, json, {
      logError: true,
      schemaName: "CompanySchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static validateJSONArray(jsonArray: unknown): CompanyJSON[] {
    return parseOrThrow(z.array(CompanySchema.CompanyJSONZ), jsonArray, {
      logError: true,
      schemaName: "CompanySchema",
      operation: "validateJSONArray",
      payloadSample: jsonArray,
    });
  }

  public static isValid(values: unknown): values is CompanyParams {
    try {
      CompanySchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is CompanyJSON {
    try {
      CompanySchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: CompanyJSON): CompanyParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(CompanySchema.CompanyJSONZ, json, {
      logError: true,
      schemaName: "CompanySchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return CompanySchema.codec.decode(parsed);
  }

  public static serialize(values: CompanyParams): CompanyJSON {
    if (isNullOrUndefined(values)) return values;
    const parsed = CompanySchema.validate(values);
    if (isNullOrUndefined(parsed)) return parsed;
    return CompanySchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(CompanySchema.CompanyJSONZ, CompanySchema.CompanyZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id)!,
      legalEntityId: Guid.fromJSON(data.legalEntityId)!,
      contactId: Guid.fromJSON(data.contactId) ?? undefined,
      isVisible: data.isVisible ? Boolean(data.isVisible) : false,
      profile: CompanyProfile.fromJSON(data) ?? undefined,
    }),
    encode: (company) => ({
      ...CompanyProfileSchema.serialize(company.profile!),
      id: company.id.toJSON(),
      legalEntityId: company.legalEntityId.toJSON(),
      contactId: company.contactId?.toJSON(),
      isVisible: company.isVisible,
    }),
  });
}
